"use client"
import { useState, useEffect, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBookOpen, faFilter, faTimes, faFileExcel, faInr, faDownload } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import { baseUrl } from "../../config/baseUrl"
import moment from "moment/moment"
import PermissionGuard from "../PermissionGuard/PermissionGuard"
import { PERMISSIONS } from "../../utils/permissions"

export default function LibraryReport() {
    const [libraries, setLibraries] = useState([])
    const [loading, setLoading] = useState(false)
    const [categoryFilter, setCategoryFilter] = useState("")
    const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"))
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))
    const [reportSummary, setReportSummary] = useState({
        totalRevenue: 0,
        totalCommission: 0,
        totalAmountToBePaid: 0,
        totalStudents: 0,
    })

    useEffect(() => {
        fetchLibraryReport()
    }, [categoryFilter, startDate, endDate])

    const fetchLibraryReport = async () => {
        try {
            setLoading(true)
            const url = `${baseUrl}/admin/dashboard/recent-libraries?startDate=${startDate}&endDate=${endDate}`

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })
            const data = await response.json()

            if (data) {
                let libraryData = data.data || []

                // Apply category filter
                if (categoryFilter) {
                    libraryData = libraryData.filter((lib) => lib.category?.toLowerCase() === categoryFilter.toLowerCase())
                }

                setLibraries(libraryData)

                // Calculate summary
                const summary = libraryData.reduce(
                    (acc, lib) => {
                        const totalAmount = (lib.totalAmountOnline || 0) + (lib.totalAmountOffline || 0)
                        const amountToPaid = totalAmount - (lib.totalCommission || 0)
                        return {
                            totalRevenue: acc.totalRevenue + totalAmount,
                            totalCommission: acc.totalCommission + (lib.totalCommission || 0),
                            totalAmountToBePaid: acc.totalAmountToBePaid + amountToPaid,
                            totalStudents: acc.totalStudents + (lib.totalStudents || 0),
                        }
                    },
                    { totalRevenue: 0, totalCommission: 0, totalAmountToBePaid: 0, totalStudents: 0 },
                )

                setReportSummary(summary)
            } else {
                toast.error(data.message || "Failed to fetch library report")
                setLibraries([])
            }
        } catch (error) {
            console.error("Error fetching library report:", error)
            toast.error("Failed to fetch library report")
            setLibraries([])
        } finally {
            setLoading(false)
        }
    }

    const handleClearFilters = () => {
        setCategoryFilter("")
        setStartDate(moment().format("YYYY-MM-DD"))
        setEndDate(moment().format("YYYY-MM-DD"))
    }

    const handleExportExcel = async () => {
        try {
            const url = `${baseUrl}/admin/dashboard/recent-libraries-excel?startDate=${startDate}&endDate=${endDate}`

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (response.ok) {
                const blob = await response.blob()
                const downloadUrl = URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = downloadUrl
                link.download = `library-report-${moment().format("YYYY-MM-DD")}.xlsx`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(downloadUrl)
                toast.success("Library report exported successfully!")
            } else {
                toast.error("Failed to export library report")
            }
        } catch (error) {
            console.error("Error exporting library report:", error)
            toast.error("Failed to export library report")
        }
    }

    const handleDownloadLibraryExcel = async (libraryId) => {
        try {
            const url = `${baseUrl}/admin/dashboard/recent-libraries-excel/${libraryId}?startDate=${startDate}&endDate=${endDate}`

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: localStorage.getItem("token"),
                },
            })

            if (response.ok) {
                const blob = await response.blob()
                const downloadUrl = URL.createObjectURL(blob)
                const link = document.createElement("a")
                link.href = downloadUrl
                link.download = `library-${libraryId}-${moment().format("YYYY-MM-DD")}.xlsx`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(downloadUrl)
                toast.success("Library report downloaded successfully!")
            } else {
                toast.error("Failed to download library report")
            }
        } catch (error) {
            console.error("Error downloading library report:", error)
            toast.error("Failed to download library report")
        }
    }

    const getActiveFiltersCount = () => {
        let count = 0
        if (categoryFilter) count++
        const currentDate = moment().format("YYYY-MM-DD")
        if (startDate !== currentDate) count++
        if (endDate !== currentDate) count++
        return count
    }

    const getDateRangeDisplay = () => {
        if (startDate === endDate) {
            return moment(startDate).format("D MMM YYYY")
        } else {
            return `${moment(startDate).format("D MMM YYYY")} - ${moment(endDate).format("D MMM YYYY")}`
        }
    }

    const scrollContainerRef = useRef(null)

    useEffect(() => {
        const slider = scrollContainerRef.current
        if (!slider) return

        let isDown = false
        let startX
        let scrollLeft

        const mouseDownHandler = (e) => {
            isDown = true
            startX = e.pageX - slider.offsetLeft
            scrollLeft = slider.scrollLeft
            slider.classList.add("dragging")
        }

        const mouseLeaveHandler = () => {
            isDown = false
            slider.classList.remove("dragging")
        }

        const mouseUpHandler = () => {
            isDown = false
            slider.classList.remove("dragging")
        }

        const mouseMoveHandler = (e) => {
            if (!isDown) return
            e.preventDefault()
            const x = e.pageX - slider.offsetLeft
            const walk = (x - startX) * 1.5
            slider.scrollLeft = scrollLeft - walk
        }

        slider.addEventListener("mousedown", mouseDownHandler)
        slider.addEventListener("mouseleave", mouseLeaveHandler)
        slider.addEventListener("mouseup", mouseUpHandler)
        slider.addEventListener("mousemove", mouseMoveHandler)

        return () => {
            slider.removeEventListener("mousedown", mouseDownHandler)
            slider.removeEventListener("mouseleave", mouseLeaveHandler)
            slider.removeEventListener("mouseup", mouseUpHandler)
            slider.removeEventListener("mousemove", mouseMoveHandler)
        }
    }, [])

    return (
        <>
            <section className="main-sec">
                <div className="container-fluid">
                    {/* Header */}
                    <div className="row mb-4">
                        <div className="col-lg-8">
                            <div className="dashboard-title">
                                <h4 className="dash-head mb-2">
                                    <FontAwesomeIcon icon={faBookOpen} className="me-2" />
                                    Library Report
                                </h4>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="export-buttons">

                                <PermissionGuard permission={PERMISSIONS.LIBRARY_REPORT_EXPORT}>
                                    <button className="btn btn-success btn-sm me-2" onClick={handleExportExcel}>
                                        <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                                        Export Excel
                                    </button>
                                </PermissionGuard>


                            </div>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    {/* <div className="row mb-4">
                        <div className="col-lg-12">
                            <div className="summary-cards-grid">
                                <div className="summary-card total-revenue">
                                    <div className="summary-icon">
                                        <FontAwesomeIcon icon={faInr} />
                                    </div>
                                    <div className="summary-content">
                                        <h6 className="text-white">Total Revenue</h6>
                                        <h4 className="text-white">₹{reportSummary.totalRevenue.toLocaleString()}</h4>
                                    </div>
                                </div>
                                <div className="summary-card total-commission">
                                    <div className="summary-icon">
                                        <FontAwesomeIcon icon={faInr} />
                                    </div>
                                    <div className="summary-content">
                                        <h6 className="text-white">Total Platform Fees</h6>
                                        <h4 className="text-white">₹{reportSummary.totalCommission.toLocaleString()}</h4>
                                    </div>
                                </div>
                                <div className="summary-card remaining-amount">
                                    <div className="summary-icon">
                                        <FontAwesomeIcon icon={faInr} />
                                    </div>
                                    <div className="summary-content">
                                        <h6 className="text-white">Amount to be Paid</h6>
                                        <h4 className="text-white">₹{reportSummary.totalAmountToBePaid.toLocaleString()}</h4>
                                    </div>
                                </div>
                                <div className="summary-card payment-status">
                                    <div className="summary-icon">
                                        <FontAwesomeIcon icon={faBookOpen} />
                                    </div>
                                    <div className="summary-content">
                                        <h6 className="text-white">Total Students</h6>
                                        <h4 className="text-white">{reportSummary.totalStudents}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> */}

                    {/* Filters Card */}
                    <div className="card filter-card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">
                                <FontAwesomeIcon icon={faFilter} className="me-2" />
                                Filters{" "}
                                {getActiveFiltersCount() > 0 && (
                                    <span className="badge bg-primary ms-2">{getActiveFiltersCount()}</span>
                                )}
                            </h6>
                            {getActiveFiltersCount() > 0 && (
                                <button className="btn btn-sm btn-outline-secondary" onClick={handleClearFilters}>
                                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                                    Clear All
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-3 col-lg-2">
                                    <label className="form-label small fw-medium">Category</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                    >
                                        <option value="">All Categories</option>
                                        <option value="premium">Premium</option>
                                        <option value="standard">Standard</option>
                                    </select>
                                </div>
                                <div className="col-md-3 col-lg-2">
                                    <label className="form-label small fw-medium">Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3 col-lg-2">
                                    <label className="form-label small fw-medium">End Date</label>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {getActiveFiltersCount() > 0 && (
                        <div className="active-filters-display mb-3">
                            <div className="d-flex flex-wrap gap-2 align-items-center">
                                <small className="text-muted fw-medium">Active Filters:</small>
                                {categoryFilter && <span className="filter-tag">Category: {categoryFilter}</span>}
                                {startDate && <span className="filter-tag">From: {moment(startDate).format("MMM DD, YYYY")}</span>}
                                {endDate && <span className="filter-tag">To: {moment(endDate).format("MMM DD, YYYY")}</span>}
                            </div>
                        </div>
                    )}

                    {/* Results Summary */}
                    <div className="results-summary mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">
                                Library Records
                                <span className="text-muted ms-2">({libraries.length} results)</span>
                            </h6>
                        </div>
                    </div>

                    {/* Table Card */}
                    <div className="card table-card">
                        <div className="table-responsive" ref={scrollContainerRef}>
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-header">
                                    <tr>
                                        <th className="sticky-col" rowSpan="2">
                                            Library
                                        </th>
                                        <th rowSpan="2">Category</th>
                                        <th colSpan="2">Mode of Payment</th>
                                        <th rowSpan="2">Date</th>
                                        <th rowSpan="2">No. Of Admission (Students)</th>
                                        <th rowSpan="2">FIXED Amount</th>
                                        <th rowSpan="2">Platform Fees</th>
                                        <th rowSpan="2">Total Amount</th>
                                        <th rowSpan="2">Amount to be Paid</th>
                                        <PermissionGuard permission={PERMISSIONS.LIBRARY_REPORT_EXPORT}>
                                            <th rowSpan="2">Action</th>
                                        </PermissionGuard>
                                    </tr>
                                    <tr>
                                        <th>Online</th>
                                        <th>Offline</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="11" className="text-center py-5">
                                                <div className="loading-container">
                                                    <div className="spinner-border text-primary mb-3" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mb-0 text-muted">Loading library report...</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : libraries.length === 0 ? (
                                        <tr>
                                            <td colSpan="11" className="text-center py-5">
                                                <div className="empty-state">
                                                    <FontAwesomeIcon icon={faBookOpen} size="3x" className="text-muted mb-3" />
                                                    <h5 className="text-muted">No Library Records Found</h5>
                                                    <p className="text-muted mb-0">
                                                        {getActiveFiltersCount() > 0
                                                            ? "Try adjusting your filter criteria"
                                                            : "No library records available at the moment"}
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        libraries.map((library, index) => {
                                            const totalAmount = (library.totalAmountOnline || 0) + (library.totalAmountOffline || 0)
                                            const amountToPaid = totalAmount - (library.totalCommission || 0)
                                            return (
                                                <tr key={index}>
                                                    <td className="sticky-col">
                                                        <strong>{library.libraryName || "N/A"}</strong>
                                                    </td>
                                                    <td>
                                                        <span className={`category-badge ${library.category?.toLowerCase() || "standard"}`}>
                                                            {library.category || "Standard"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="amount-badge online">₹{library.totalAmountOnline || 0}</span>
                                                    </td>
                                                    <td>
                                                        <span className="amount-badge offline">₹{library.totalAmountOffline || 0}</span>
                                                    </td>
                                                    <td>
                                                        <span className="date-text">{getDateRangeDisplay()}</span>
                                                    </td>
                                                    <td>
                                                        <span className="student-count">{library.totalStudents || 0}</span>
                                                    </td>
                                                    <td>
                                                        <span className="fixed-amount">₹{library.adminCommission?.value || 0}</span>
                                                    </td>
                                                    <td>
                                                        <span className="platform-fee">₹{library.totalCommission || 0}</span>
                                                    </td>
                                                    <td>
                                                        <span className="total-amount">₹{totalAmount}</span>
                                                    </td>
                                                    <td>
                                                        <span className="amount-to-paid">₹{amountToPaid}</span>
                                                    </td>

                                                    <PermissionGuard permission={PERMISSIONS.LIBRARY_REPORT_EXPORT}>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleDownloadLibraryExcel(library._id)}
                                                            >
                                                                <FontAwesomeIcon icon={faDownload} className="me-1" />
                                                            </button>
                                                        </td>
                                                    </PermissionGuard>


                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>

            <style>
                {`
        .table-responsive {
          cursor: grab;
        }

        .table-responsive.dragging {
          cursor: grabbing;
          user-select: none;
        }

        .main-sec {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding: 2rem 0;
        }

        .dashboard-title .dash-head {
          color: #2c3e50;
          font-weight: 600;
        }

        .export-buttons {
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
        }

        .summary-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        /* Updated summary cards to use calm, elegant gradients */
        .summary-card {
          background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
          color: white;
          padding: 1.25rem;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          transition: transform 0.2s ease;
        }

        .summary-card:hover {
          transform: translateY(-2px);
        }

        .summary-card.total-revenue {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .summary-card.total-commission {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }

        .summary-card.remaining-amount {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }

        .summary-card.payment-status {
          background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
        }

        .summary-icon {
          background: rgba(255,255,255,0.15);
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
        }

        .summary-content h6 {
          margin: 0;
          font-size: 0.875rem;
          opacity: 0.95;
          font-weight: 500;
        }

        .summary-content h4 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .filter-card {
          border: none;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }

        .filter-card .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e5e7eb;
        }

        /* Updated active filters to use calm blue tones */
        .active-filters-display {
          background-color: #eff6ff;
          border: 1px solid #dbeafe;
          border-radius: 0.375rem;
          padding: 0.75rem;
        }

        .filter-tag {
          background-color: #60a5fa;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .results-summary {
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          padding: 0.75rem 1rem;
        }

        .table-card {
          border: none;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }

        .table-header th {
          background-color: #f9fafb;
          border-bottom: 2px solid #e5e7eb;
          font-weight: 600;
          font-size: 0.8rem;
          padding: 0.75rem 0.5rem;
          white-space: nowrap;
          text-align: center;
          color: #374151;
        }

        .table td {
          padding: 0.75rem 0.5rem;
          vertical-align: middle;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.875rem;
          text-align: center;
        }

        .table tbody tr:hover {
          background-color: #f9fafb;
        }

        .sticky-col {
          position: sticky;
          left: 0;
          background: white;
          z-index: 10;
          box-shadow: 2px 0 5px rgba(0, 0, 0, 0.04);
        }

        .table-header .sticky-col {
          z-index: 11;
        }

        .table tbody tr:hover .sticky-col {
          background: #f9fafb;
        }

        /* Updated category badges to use calm, muted colors */
        .category-badge {
          padding: 5px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          text-transform: capitalize;
        }

        .category-badge.premium {
          background-color: #6366f1;
          color: white;
        }

        .category-badge.standard {
          background-color: #f59e0b;
          color: white;
        }

        /* Updated amount badges to use softer, elegant colors */
        .amount-badge {
          padding: 5px 10px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
        }

        .amount-badge.online {
          color: #000;
        }

        .amount-badge.offline {
          color: #000;
        }

        .student-count {
          font-weight: 600;
          color: #4b5563;
          font-size: 14px;
        }

        /* Added date-text styling */
        .date-text {
          font-weight: 500;
          color: #6b7280;
          font-size: 13px;
        }

        /* Updated amount colors to be more muted and elegant */
        .fixed-amount {
          font-weight: 600;
        }

        .platform-fee {
          font-weight: 600;
        }

        .total-amount {
          font-weight: 700;
          font-size: 15px;
        }

        .amount-to-paid {
          font-weight: 700;
          font-size: 15px;
        }

        .loading-container,
        .empty-state {
          padding: 2rem;
        }

        @media (max-width: 1200px) {
          .table-header th,
          .table td {
            padding: 0.5rem 0.25rem;
            font-size: 0.8rem;
          }
        }

        @media (max-width: 768px) {
          .summary-cards-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .export-buttons {
            justify-content: center;
            margin-top: 1rem;
          }

          .table-responsive {
            font-size: 0.75rem;
          }

          .table-header th,
          .table td {
            padding: 0.4rem 0.2rem;
          }
        }

        @media (max-width: 576px) {
          .main-sec {
            padding: 1rem 0;
          }

          .summary-cards-grid {
            grid-template-columns: 1fr;
          }

          .summary-card {
            padding: 1rem;
          }

          .summary-content h4 {
            font-size: 1.25rem;
          }

          .filter-card .card-body .row {
            gap: 0.5rem;
          }
        }
        `}
            </style>
        </>
    )
}
